import React, { useState } from "react";
import { DashboardLayout } from "components/DashboardLayout";
import Async from "components/DesignSystem/AsyncComponents/Async";
import { Button } from "components/DesignSystem/Button/Button";
import { useModal } from "hooks/useModal";
import { usePageTitle } from "hooks/usePageTitle";
import { useRoleBasedView } from "hooks/useRoleBasedView";
import { useToast } from "hooks/useToast";
import { useDispatch } from "react-redux";
import { useAddProductsToCartAndCheckoutMutation } from "store/apis/billing";
import { Product, useGetAllProductsQuery } from "store/apis/products";
import { useLazyGetTaskByRequestIdQuery } from "store/apis/registrations";
import { taskAlarmApis, useGetTaskListForUserQuery } from "store/apis/taskList";
import { BillingInvoice } from "types/Models/billing";
import { Task } from "types/Models/task";
import { BackendError } from "types/utils/error";
import { stateUpdate } from "apis/stateUpdate";
import { getByValue } from "components/fileTax/getIndex";
import AddTaskEntityModal from "components/tasks/modal/AddTaskEntityModal";
import RAndDBG from "static/images/RAndDBG.svg";
import Peyaj from "static/images/Peyaj.svg";
import Ruti from "static/images/Ruti.svg";
import { useCurrentGroupContext } from "hooks/useCurrentGroupContext";
import {
  DISSOLUTION,
  US_HOLDCO_INCORPORATION,
  US_SUBSIDARY_INCORPORATION,
} from "types/Models/services";
import { CheckoutModal } from "components/CheckoutModal/CheckoutModal";
import { OtherFAQs } from "components/TaxOthers/OtherFAQs";
import { IncorporationDissolutionTaskTable } from "components/TaxOthers/IncorporateDissolutionTaskTable";
import { AddIncorporateFiling } from "components/TaxOthers/AddIncorporateFiling";
import { DISCUSS_US_INDIA_STRUCTURING_WITH_ANAND } from "constants/meetingRequestUrls";
import { CalendlyModal } from "components/DesignSystem/CalendlyModal/CalendlyModal";
import dayjs from "dayjs";

const dissolutionProductId = process.env.PUBLIC_DISSOLUTION_PRODUCT_ID;

const pageTitles = {
  incorporation: "Tax Incorporation",
  dissolution: "Tax Dissolution",
};

const baseTaskKeys = {
  incorporation: [US_HOLDCO_INCORPORATION, US_SUBSIDARY_INCORPORATION],
  dissolution: [DISSOLUTION],
};

const descriptions = {
  incorporation: {
    title: "Assisted Incorporation",
    price: (
      <>
        <b>$999/</b>per incorporation
      </>
    ),
    description:
      "Inkle helps startup founders, both in the US and abroad (including specialized support for India), to compliantly establish a US Delaware C-Corp through Stripe Atlas or Clerky, with options for parent or subsidiary entities.",
    image: Peyaj,
  },
  dissolution: {
    title: "Assisted Dissolution",
    price: (
      <>
        <b>$350/per</b> dissolution
      </>
    ),
    description:
      "Inkle facilitates the compliant filing of your Dissolution Certificate with the Delaware Secretary of State, prepares Stockholder Consent and Board of Directors Resolutions, closes your IRS tax account, and provides a comprehensive checklist and guidance for winding down all other activities and accounts.",
    image: Ruti,
  },
};

export const TaxOthers = ({
  type,
}: {
  type: "incorporation" | "dissolution";
}) => {
  usePageTitle(pageTitles[type]);
  const { uuid: groupId, entities } = useCurrentGroupContext();
  const entityId = entities[0]?.uuid; // only to get products
  const [selectedEntityId, setSelectedEntityId] = useState("");
  const { isCustomer, isForeignCA } = useRoleBasedView();
  const { data: products } = useGetAllProductsQuery(
    {
      groupId,
      entityId,
      product_type: "TAX",
    },
    { skip: !groupId || !entityId }
  );
  const {
    data,
    isLoading: isTaskLoading,
    isFetching: isRefetchingTask,
    isSuccess: isTaskSuccess,
  } = useGetTaskListForUserQuery(
    {
      taskCategoryType: "TAX,RND",
      baseTaskKey: baseTaskKeys[type].join(","),
    },
    { skip: !isCustomer, refetchOnMountOrArgChange: true }
  );

  const { tasks: taskList = [] } = (data || {}) as {
    tasks?: Task[];
  };

  const [getTaskByRequestId] = useLazyGetTaskByRequestIdQuery();
  const taxProducts = Object.values(products?.TAX || {}).flat();

  const dissolutionProduct = taxProducts.find(
    (product) => (product as Product).uuid === dissolutionProductId
  );
  const showCalendly = useModal();
  const addTaskModal = useModal();
  const addIncorporationModal = useModal();
  const { alertToast } = useToast();
  const dispatch = useDispatch();

  const [createProductInvoice, { data: productInvoice }] =
    useAddProductsToCartAndCheckoutMutation();
  const filingPaymentModal = useModal();
  const productDetails = descriptions[type];

  const handleFormSubmit = async ({
    entity,
    productId,
  }: {
    entity: string;
    productId: string;
  }) => {
    setSelectedEntityId(entity);
    const productToAdd = taxProducts.find(
      (product) => (product as Product).uuid === productId
    ) as Product;
    if (groupId && productToAdd) {
      try {
        const payload = {
          entity_id: entity,
          product_content_type_id: productToAdd.product_content_type_id,
          product_id: productToAdd.uuid,
          quantity: 1,
          season: productToAdd.seasons?.[0]?.toString() || null,
        };

        await createProductInvoice({
          groupId,
          entityId: entity!,
          productData: [payload],
          from_other: true,
        }).unwrap();
        filingPaymentModal.open();
      } catch (error) {
        alertToast({ message: (error as BackendError).data?.error?.message });
      } finally {
        addTaskModal.close();
        addIncorporationModal.close();
      }
    }
  };

  const getNewTaskDetail = async () => {
    try {
      const taskData = await getTaskByRequestId({
        groupId,
        requestId: productInvoice?.request_id!,
        entityId: selectedEntityId!,
      }).unwrap();

      if (taskData && taskData.length > 0) {
        const task = taskData[0];
        const current = task?.task_states?.[0].uuid;
        const check = new Map();
        const indexing = new Map();

        task?.task_states?.forEach((element, index) => {
          check.set(element.uuid, element.type);
          indexing.set(element.uuid, index);
        });

        const nextStateUuid = getByValue(indexing, indexing.get(current) + 1);
        await stateUpdate({ taskId: task.uuid, id: nextStateUuid });
      }

      dispatch(taskAlarmApis.util.invalidateTags(["TaskList"]));
    } catch (error) {
      alertToast({
        message: (error as BackendError)?.data?.error?.message,
      });
    } finally {
      filingPaymentModal.close();
    }
  };

  return (
    <DashboardLayout>
      <Async.Root
        isEmpty={taskList.length === 0}
        isLoading={isTaskLoading || isRefetchingTask}
        isSuccess={isTaskSuccess}
      >
        <Async.Empty>
          <div className="t-flex t-h-[282px] t-w-full t-border t-border-solid t-border-neutral-10 t-rounded-lg">
            <div className="t-p-8 t-flex-1 t-flex t-flex-col t-justify-between">
              <div className="t-space-y-4 t-flex-1">
                <div className="t-text-subtext t-text-text-100">
                  {productDetails.title}
                </div>
                <div className="t-text-subtitle t-text-text-60">
                  {productDetails.price}
                </div>
                <div className="t-text-body-sm t-text-text-30">
                  {productDetails.description}
                </div>
              </div>
              <div className="t-flex t-gap-4 t-h-10">
                <Button
                  customType="primary"
                  onClick={
                    type === "incorporation"
                      ? addIncorporationModal.open
                      : addTaskModal.open
                  }
                >
                  Get Started
                </Button>
                <Button customType="link" onClick={showCalendly.open}>
                  Unsure of your structure? Book a call
                </Button>
              </div>
            </div>
            <div className="t-relative t-h-full t-w-[380px] t-justify-center t-items-center t-flex -t-mr-[1.5px]">
              <img
                src={RAndDBG}
                className="t-absolute t-h-full t-w-full t-rounded-r-[9px] t-z-[-1]"
                alt=""
              />
              <img src={productDetails.image} alt="" />
            </div>
          </div>
          {type === "incorporation" && <OtherFAQs />}
        </Async.Empty>
        <Async.Success>
          <IncorporationDissolutionTaskTable taskList={taskList} />
          {taskList?.[0]?.uuid && type === "incorporation" && (
            <OtherFAQs taskId={taskList[0].uuid} />
          )}
        </Async.Success>
      </Async.Root>
      <CalendlyModal
        {...showCalendly}
        calendlyLink={DISCUSS_US_INDIA_STRUCTURING_WITH_ANAND}
      />
      {addTaskModal.isOpen && (
        <AddTaskEntityModal
          to=""
          taskTemplateId={dissolutionProductId}
          handleClose={addTaskModal.close}
          handleFormSubmit={({ entity }) =>
            dissolutionProduct &&
            handleFormSubmit({
              entity,
              productId: dissolutionProductId as string,
            })
          }
        />
      )}
      {addIncorporationModal.isOpen && (
        <AddIncorporateFiling
          {...addIncorporationModal}
          handleSubmit={handleFormSubmit}
          openCalendly={showCalendly.open}
        />
      )}
      {filingPaymentModal.isOpen &&
        Boolean(productInvoice?.invoice) &&
        selectedEntityId && (
          <CheckoutModal
            entityId={selectedEntityId}
            type="invoices"
            onInvoicePaid={getNewTaskDetail}
            open={filingPaymentModal.isOpen}
            onClose={filingPaymentModal.close}
            invoices={[productInvoice?.invoice as BillingInvoice]}
            invoiceRequestId={productInvoice?.request_id}
            payerUserType={isForeignCA && "FCA"}
          />
        )}
    </DashboardLayout>
  );
};
